import { StatCard } from '@/components/ui/stat-card';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { DollarSign, Bot, Zap, BarChart2, Users } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useExecutiveSummary, useVendorRanking } from '@/hooks/useSubscriptions';
import type { SubscriptionExecutiveSummary } from '../../../../shared/types';
import { WinclapLoader } from '@/components/ui/winclap-loader';

const fmt = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const fmtPct = (n: number | null) =>
  n == null ? '—' : `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;

const fmtShortMonth = (m: string) => {
  const [y, mo] = m.split('-');
  return new Date(+y, +mo - 1).toLocaleString('en-US', { month: 'short', year: '2-digit' });
};


interface Forecasts {
  simpleRunRate: number;
  aiSimpleRunRate: number;
  currentRunRate: number;
  aiCurrentRunRate: number;
  growthAdjusted: number | null;
  aiGrowthAdjusted: number | null;
  months: number;
  avgMoMGrowth: number;
  avgAIMoMGrowth: number;
  remainingMonths: number;
  lastDataMonth: string;
}

function computeForecasts(rows: SubscriptionExecutiveSummary[]): Forecasts | null {
  if (rows.length === 0) return null;
  const totalYTD = rows.reduce((s, r) => s + r.Total_Subscriptions_Amount, 0);
  const aiYTD = rows.reduce((s, r) => s + r.AI_Tools_Amount, 0);
  const months = rows.length;
  const latest = rows[rows.length - 1];

  const simpleRunRate = (totalYTD / months) * 12;
  const aiSimpleRunRate = (aiYTD / months) * 12;
  const currentRunRate = latest.Total_Subscriptions_Amount * 12;
  const aiCurrentRunRate = latest.AI_Tools_Amount * 12;

  const lastDataYear = parseInt(latest.Month.split('-')[0]);
  const lastDataMonth = parseInt(latest.Month.split('-')[1]);
  const remainingMonths = Math.max(0, (lastDataYear * 12 + 12) - (lastDataYear * 12 + lastDataMonth));

  const growthRates = rows.slice(1).map((r, i) => {
    const prev = rows[i].Total_Subscriptions_Amount;
    return prev > 0 ? (r.Total_Subscriptions_Amount - prev) / prev : 0;
  });
  const aiGrowthRates = rows.slice(1).map((r, i) => {
    const prev = rows[i].AI_Tools_Amount;
    return prev > 0 ? (r.AI_Tools_Amount - prev) / prev : 0;
  });
  const avgMoMGrowth = growthRates.length > 0
    ? growthRates.reduce((s, v) => s + v, 0) / growthRates.length
    : 0;
  const avgAIMoMGrowth = aiGrowthRates.length > 0
    ? aiGrowthRates.reduce((s, v) => s + v, 0) / aiGrowthRates.length
    : 0;

  let growthAdjusted: number | null = null;
  let aiGrowthAdjusted: number | null = null;

  if (months >= 3) {
    let projSubs = totalYTD;
    let projAI = aiYTD;
    let cur = latest.Total_Subscriptions_Amount;
    let curAI = latest.AI_Tools_Amount;
    for (let i = 0; i < remainingMonths; i++) {
      cur = cur * (1 + avgMoMGrowth);
      curAI = curAI * (1 + avgAIMoMGrowth);
      projSubs += cur;
      projAI += curAI;
    }
    growthAdjusted = projSubs;
    aiGrowthAdjusted = projAI;
  }

  return {
    simpleRunRate, aiSimpleRunRate, currentRunRate, aiCurrentRunRate,
    growthAdjusted, aiGrowthAdjusted, months,
    avgMoMGrowth, avgAIMoMGrowth, remainingMonths,
    lastDataMonth: latest.Month,
  };
}

// Build chart data: actual months + projected months to Dec
function buildForecastChartData(rows: SubscriptionExecutiveSummary[], forecasts: Forecasts) {
  const data: Array<{
    month: string;
    actual_total?: number;
    actual_ai?: number;
    proj_simple?: number;
    proj_simple_ai?: number;
    proj_current?: number;
    proj_current_ai?: number;
    proj_growth?: number;
    proj_growth_ai?: number;
    isProjected: boolean;
  }> = [];

  // Actual months
  for (const r of rows) {
    data.push({
      month: fmtShortMonth(r.Month),
      actual_total: r.Total_Subscriptions_Amount,
      actual_ai: r.AI_Tools_Amount,
      isProjected: false,
    });
  }

  if (forecasts.remainingMonths === 0) return data;

  // Projected months
  const latest = rows[rows.length - 1];
  const curCurrent = latest.Total_Subscriptions_Amount;
  const curCurrentAI = latest.AI_Tools_Amount;
  let curGrowth = latest.Total_Subscriptions_Amount;
  let curGrowthAI = latest.AI_Tools_Amount;

  const [lastY, lastM] = latest.Month.split('-').map(Number);

  for (let i = 1; i <= forecasts.remainingMonths; i++) {
    const projMonth = lastM + i;
    const projYear = lastY + Math.floor((projMonth - 1) / 12);
    const projMonthNorm = ((projMonth - 1) % 12) + 1;
    const label = new Date(projYear, projMonthNorm - 1).toLocaleString('en-US', { month: 'short', year: '2-digit' });

    // Simple: use avg monthly (flat)
    const avgMonthly = rows.reduce((s, r) => s + r.Total_Subscriptions_Amount, 0) / rows.length;
    const avgAIMonthly = rows.reduce((s, r) => s + r.AI_Tools_Amount, 0) / rows.length;

    // Current: last month constant
    // Growth: apply MoM rate
    curGrowth = curGrowth * (1 + forecasts.avgMoMGrowth);
    curGrowthAI = curGrowthAI * (1 + forecasts.avgAIMoMGrowth);

    data.push({
      month: label,
      proj_simple: avgMonthly,
      proj_simple_ai: avgAIMonthly,
      proj_current: curCurrent,
      proj_current_ai: curCurrentAI,
      proj_growth: forecasts.growthAdjusted != null ? curGrowth : undefined,
      proj_growth_ai: forecasts.aiGrowthAdjusted != null ? curGrowthAI : undefined,
      isProjected: true,
    });
  }

  return data;
}

export function OverviewTab() {
  const { t } = useTranslation();
  const { data: summary = [], isLoading: loadingSummary } = useExecutiveSummary();
  const { data: vendors = [], isLoading: loadingVendors } = useVendorRanking();

  const latest = summary[summary.length - 1];
  const forecasts = useMemo(() => computeForecasts(summary), [summary]);

  const ytdTotal = summary.reduce((s, r) => s + r.Total_Subscriptions_Amount, 0);
  const ytdAI = summary.reduce((s, r) => s + r.AI_Tools_Amount, 0);

  const chartData = summary.map((r) => ({
    month: fmtShortMonth(r.Month),
    'Total Subs': r.Total_Subscriptions_Amount,
    'AI Tools': r.AI_Tools_Amount,
    'AI Share %': +(r.AI_Share * 100).toFixed(1),
  }));

  const forecastChartData = useMemo(
    () => (forecasts ? buildForecastChartData(summary, forecasts) : []),
    [summary, forecasts]
  );

  // First projected month index for reference line
  const firstProjIdx = forecastChartData.findIndex((d) => d.isProjected);
  const splitMonth = firstProjIdx > 0 ? forecastChartData[firstProjIdx].month : null;

  const topVendors = vendors.slice(0, 5);
  const topAIVendors = vendors.filter((v) => v.Is_AI_Tool).slice(0, 5);

  const insights: string[] = [];
  if (latest) {
    const aiSharePct = (latest.AI_Share * 100).toFixed(1);
    insights.push(t('subscriptions.insights.ai_share', { pct: aiSharePct }));
    if (latest.AI_Tools_MoM_Growth != null) {
      const key = latest.AI_Tools_MoM_Growth >= 0 ? 'subscriptions.insights.ai_mom_up' : 'subscriptions.insights.ai_mom_down';
      insights.push(t(key, { pct: Math.abs(latest.AI_Tools_MoM_Growth).toFixed(1) }));
    }
    const topAI = topAIVendors[0];
    if (topAI) insights.push(t('subscriptions.insights.top_ai_vendor', { vendor: topAI.Normalized_Vendor, amount: fmt(topAI.Total_Amount) }));
    if (latest.Subscriptions_MoM_Growth != null) {
      insights.push(t(latest.Subscriptions_MoM_Growth >= 0 ? 'subscriptions.insights.subs_trending_up' : 'subscriptions.insights.subs_trending_down'));
    }
  }
  if ((forecasts?.months ?? 0) < 3) {
    insights.push(t('subscriptions.insights.needs_history'));
  }

  const currentYear = new Date().getFullYear();

  if (loadingSummary) {
    return (
      <div className="flex justify-center py-16">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-4">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        <StatCard icon={DollarSign} label={t('subscriptions.kpi.total_ytd')} value={fmt(ytdTotal)} iconClassName="text-blue-500" />
        <StatCard icon={Bot} label={t('subscriptions.kpi.ai_ytd')} value={fmt(ytdAI)} iconClassName="text-purple-500" />
        <StatCard icon={BarChart2} label={t('subscriptions.kpi.ai_share')} value={latest ? `${(latest.AI_Share * 100).toFixed(1)}%` : '—'} iconClassName="text-indigo-500" />
        <StatCard
          icon={DollarSign} label={t('subscriptions.kpi.current_month')} value={latest ? fmt(latest.Total_Subscriptions_Amount) : '—'}
          trend={latest?.Subscriptions_MoM_Growth ?? null} trendLabel={(v) => `${fmtPct(v)} ${t('subscriptions.overviewtab.vs_prev_month')}`} iconClassName="text-emerald-500"
        />
        <StatCard
          icon={Bot} label={t('subscriptions.kpi.current_month_ai')} value={latest ? fmt(latest.AI_Tools_Amount) : '—'}
          trend={latest?.AI_Tools_MoM_Growth ?? null} trendLabel={(v) => `${fmtPct(v)} ${t('subscriptions.overviewtab.vs_prev_month')}`} iconClassName="text-violet-500"
        />
        <StatCard icon={Zap} label={t('subscriptions.kpi.avg_monthly')} value={forecasts ? fmt(forecasts.simpleRunRate / 12) : '—'} iconClassName="text-orange-500" />
        <StatCard icon={Zap} label={t('subscriptions.kpi.avg_monthly_ai')} value={forecasts ? fmt(forecasts.aiSimpleRunRate / 12) : '—'} iconClassName="text-amber-500" />
        <StatCard icon={BarChart2} label={t('subscriptions.kpi.active_vendors')} value={latest?.Vendors != null ? String(latest.Vendors) : '—'} iconClassName="text-cyan-500" />
        <StatCard icon={Bot} label={t('subscriptions.kpi.ai_vendors')} value={latest?.AI_Vendors != null ? String(latest.AI_Vendors) : '—'} iconClassName="text-fuchsia-500" />
        <StatCard icon={Users} label={t('subscriptions.kpi.active_users')} value={latest?.Users != null ? String(latest.Users) : '—'} iconClassName="text-teal-500" />
      </div>

      {/* Spend over time */}
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-4">{t('subscriptions.charts.monthly_spend')}</h3>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => fmt(Number(v))} />
            <Legend />
            <Line type="monotone" dataKey="Total Subs" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="AI Tools" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* AI Share over time */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-4">{t('subscriptions.charts.ai_share_pct')}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => `${v}%`} />
              <Bar dataKey="AI Share %" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Insights */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">{t('subscriptions.charts.key_insights')}</h3>
          <ul className="space-y-2">
            {insights.map((insight, i) => (
              <li key={i} className="flex gap-2 text-sm text-muted-foreground">
                <span className="text-primary mt-0.5">✦</span>
                <span>{insight}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* ── FORECASTING ── */}
      {forecasts && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold">{t('subscriptions.forecast.title')}</h2>
            <span className="text-xs text-muted-foreground">{t('subscriptions.forecast.subtitle')}</span>
          </div>

          {/* Projection chart */}
          <Card className="p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-semibold">{t('subscriptions.forecast.year_end', { year: currentYear })}</h3>
              <div className="flex gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <span className="inline-block w-6 h-0.5 bg-blue-500" /> {t('subscriptions.forecast.actual')}
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-6 border-t-2 border-dashed border-emerald-500" /> {t('subscriptions.forecast.method_simple')}
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-6 border-t-2 border-dashed border-orange-500" /> {t('subscriptions.forecast.method_current')}
                </span>
                {forecasts.growthAdjusted != null && (
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-6 border-t-2 border-dashed border-purple-500" /> {t('subscriptions.forecast.method_growth')}
                  </span>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-4">{t('subscriptions.overviewtab.total_subs_linea_solida_real_lineas')}</p>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={forecastChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v, name) => [fmt(Number(v)), name]}
                  labelFormatter={(l) => l}
                />
                {splitMonth && (
                  <ReferenceLine
                    x={splitMonth}
                    stroke="#94a3b8"
                    strokeDasharray="4 4"
                    label={{ value: '▶ proyectado', position: 'top', fontSize: 10, fill: '#94a3b8' }}
                  />
                )}
                <Line type="monotone" dataKey="actual_total" name="Real total" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                <Line type="monotone" dataKey="proj_simple" name={t('subscriptions.forecast.method_simple')} stroke="#10b981" strokeWidth={1.5} strokeDasharray="5 4" dot={false} connectNulls />
                <Line type="monotone" dataKey="proj_current" name={t('subscriptions.forecast.method_current')} stroke="#f97316" strokeWidth={1.5} strokeDasharray="5 4" dot={false} connectNulls />
                {forecasts.growthAdjusted != null && (
                  <Line type="monotone" dataKey="proj_growth" name={t('subscriptions.forecast.method_growth')} stroke="#8b5cf6" strokeWidth={1.5} strokeDasharray="5 4" dot={false} connectNulls />
                )}
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {/* Method cards + assumptions */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {/* Simple run-rate */}
            <Card className="p-4 border-l-4 border-l-emerald-500">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">{t('subscriptions.forecast.method_simple')}</p>
                <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-700">conservador</Badge>
              </div>
              <div className="space-y-1 mb-3">
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">{t('subscriptions.forecast.total_subs')}</span>
                  <span className="text-sm font-bold">{fmt(forecasts.simpleRunRate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">{t('subscriptions.forecast.ai_tools')}</span>
                  <span className="text-sm font-bold text-purple-600">{fmt(forecasts.aiSimpleRunRate)}</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{t('subscriptions.forecast.method_simple_desc')}</p>
            </Card>

            {/* Current run-rate */}
            <Card className="p-4 border-l-4 border-l-orange-500">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-orange-600 uppercase tracking-wide">{t('subscriptions.forecast.method_current')}</p>
                <Badge variant="outline" className="text-xs border-orange-300 text-orange-700">momentum</Badge>
              </div>
              <div className="space-y-1 mb-3">
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">{t('subscriptions.forecast.total_subs')}</span>
                  <span className="text-sm font-bold">{fmt(forecasts.currentRunRate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">{t('subscriptions.forecast.ai_tools')}</span>
                  <span className="text-sm font-bold text-purple-600">{fmt(forecasts.aiCurrentRunRate)}</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{t('subscriptions.forecast.method_current_desc')}</p>
            </Card>

            {/* Growth-adjusted */}
            <Card className={`p-4 border-l-4 ${forecasts.growthAdjusted != null ? 'border-l-purple-500' : 'border-l-muted'}`}>
              <div className="flex items-center justify-between mb-2">
                <p className={`text-xs font-semibold uppercase tracking-wide ${forecasts.growthAdjusted != null ? 'text-purple-600' : 'text-muted-foreground'}`}>
                  {t('subscriptions.forecast.method_growth')}
                </p>
                {forecasts.growthAdjusted != null && (
                  <Badge variant="outline" className="text-xs border-purple-300 text-purple-700">{t('subscriptions.overviewtab.dinamico')}</Badge>
                )}
              </div>
              {forecasts.growthAdjusted != null ? (
                <>
                  <div className="space-y-1 mb-3">
                    <div className="flex justify-between">
                      <span className="text-xs text-muted-foreground">{t('subscriptions.forecast.total_subs')}</span>
                      <span className="text-sm font-bold">{fmt(forecasts.growthAdjusted)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-muted-foreground">{t('subscriptions.forecast.ai_tools')}</span>
                      <span className="text-sm font-bold text-purple-600">{forecasts.aiGrowthAdjusted != null ? fmt(forecasts.aiGrowthAdjusted) : '—'}</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{t('subscriptions.forecast.method_growth_desc')}</p>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">{t('subscriptions.forecast.needs_more_data')}</p>
              )}
            </Card>
          </div>

          {/* Assumptions box */}
          <Card className="p-4 bg-muted/30">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{t('subscriptions.forecast.assumptions')}</p>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-4">
              <div>
                <p className="text-xs text-muted-foreground">{t('subscriptions.forecast.months_of_data')}</p>
                <p className="text-sm font-bold">{forecasts.months}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('subscriptions.forecast.avg_mom_growth')}</p>
                <p className={`text-sm font-bold ${forecasts.avgMoMGrowth >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {forecasts.avgMoMGrowth >= 0 ? '+' : ''}{(forecasts.avgMoMGrowth * 100).toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('subscriptions.forecast.ai_avg_mom_growth')}</p>
                <p className={`text-sm font-bold ${forecasts.avgAIMoMGrowth >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {forecasts.avgAIMoMGrowth >= 0 ? '+' : ''}{(forecasts.avgAIMoMGrowth * 100).toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('subscriptions.forecast.remaining_months')}</p>
                <p className="text-sm font-bold">{forecasts.remainingMonths}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Vendor rankings */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">{t('subscriptions.charts.top_vendors')}</h3>
          {loadingVendors ? (
            <WinclapLoader size={48} className="text-foreground/25 mx-auto" />
          ) : (
            <ul className="space-y-2">
              {topVendors.map((v, i) => (
                <li key={v.Normalized_Vendor} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-muted-foreground w-4">{i + 1}</span>
                    <span className="text-sm font-medium truncate">{v.Normalized_Vendor}</span>
                    {v.Is_AI_Tool && <Badge variant="secondary" className="text-xs shrink-0 bg-purple-100 text-purple-700">AI</Badge>}
                  </div>
                  <span className="text-sm font-semibold shrink-0">{fmt(v.Total_Amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">{t('subscriptions.charts.top_ai_vendors')}</h3>
          {loadingVendors ? (
            <WinclapLoader size={48} className="text-foreground/25 mx-auto" />
          ) : (
            <ul className="space-y-2">
              {topAIVendors.map((v, i) => (
                <li key={v.Normalized_Vendor} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-muted-foreground w-4">{i + 1}</span>
                    <span className="text-sm font-medium truncate">{v.Normalized_Vendor}</span>
                    {v.Is_AI_Native_Related && <Badge variant="secondary" className="text-xs shrink-0 bg-indigo-100 text-indigo-700">{t('subscriptions.overviewtab.ai_native')}</Badge>}
                  </div>
                  <span className="text-sm font-semibold shrink-0">{fmt(v.Total_Amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
